import { useEffect, useCallback, useRef, useState } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { Modal } from "antd";
import { getAccessToken } from "../config/token";

const WARNING_THRESHOLD_MINUTES = 5; // TESTING: Set to 70 to see warning immediately (change back to 5 for production)
const CHECK_INTERVAL_MS = 60000; // TESTING: Check every 10 seconds for faster feedback (change back to 60000 for production)

interface SessionExpirationState {
  isExpiringSoon: boolean;
  expiresInMinutes: number | null;
}

export const useSessionExpirationWarning = () => {
  const msalInstance = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [state, setState] = useState<SessionExpirationState>({
    isExpiringSoon: false,
    expiresInMinutes: null,
  });
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const warningModalRef = useRef<ReturnType<typeof Modal.warning> | null>(null);

  const showExpirationWarning = useCallback((minutesRemaining: number) => {
    if (warningModalRef.current) {
      return; // Modal already shown
    }

    // Check if warning was already shown for this session
    const warningShownKey = "session-warning-shown";
    if (sessionStorage.getItem(warningShownKey)) {
      return; // Already shown in this session
    }

    // Mark as shown
    sessionStorage.setItem(warningShownKey, "true");

    warningModalRef.current = Modal.warning({
      title: "Session Expiring Soon",
      content: `Your session will expire in ${minutesRemaining} minute${minutesRemaining === 1 ? "" : "s"}. Please click the Refresh button below to extend your session and continue working.`,
      okText: "Refresh Now",
      centered: true,
      onOk: () => {
        warningModalRef.current = null;
        sessionStorage.removeItem(warningShownKey);
        globalThis.location.reload();
      },
      onCancel: () => {
        warningModalRef.current = null;
        // Keep the flag so it doesn't show again until session is refreshed
      },
    });
  }, []);

  const showSessionExpiredModal = useCallback(() => {
    // Clear any existing warning modal
    if (warningModalRef.current) {
      warningModalRef.current.destroy();
      warningModalRef.current = null;
    }

    Modal.error({
      title: "Session Expired",
      content: "Your session has expired. Please log in again to continue.",
      okText: "Reload",
      centered: true,
      onOk: () => {
        globalThis.location.reload();
      },
    });
  }, []);

  const checkTokenExpiration = useCallback(async () => {
    try {
      const tokenData = await getAccessToken(msalInstance);

      if (!tokenData?.expiresOn && !tokenData?.idTokenClaims) {
        return;
      }

      const now = Math.floor(Date.now() / 1000); // Current time in seconds
      let expiresAt: number | null = null;

      // Check expiresOn first (Date object)
      if (tokenData.expiresOn) {
        expiresAt = Math.floor(tokenData.expiresOn.getTime() / 1000);
      } else if (tokenData.idTokenClaims) {
        // Fallback to idTokenClaims (Unix timestamp)
        expiresAt = tokenData.idTokenClaims;
      }

      if (!expiresAt) {
        return;
      }

      const secondsRemaining = expiresAt - now;
      const minutesRemaining = Math.floor(secondsRemaining / 60);

      // Update state
      setState({
        isExpiringSoon: minutesRemaining <= WARNING_THRESHOLD_MINUTES && minutesRemaining > 0,
        expiresInMinutes: minutesRemaining,
      });

      // Show warning modal if expiring soon
      if (
        minutesRemaining <= WARNING_THRESHOLD_MINUTES &&
        minutesRemaining > 0 &&
        !warningModalRef.current
      ) {
        showExpirationWarning(minutesRemaining);
      }

      // Session has expired
      if (secondsRemaining <= 0) {
        showSessionExpiredModal();
      }
    } catch (error) {
      // Silently handle token check errors to avoid disrupting user experience
      // Reset state on error to prevent stale warning states
      setState({
        isExpiringSoon: false,
        expiresInMinutes: null,
      });
    }
  }, [msalInstance, showExpirationWarning, showSessionExpiredModal]);

  // Start monitoring on mount
  useEffect(() => {
    // Only run session expiration checks if user is authenticated
    if (!isAuthenticated) {
      return;
    }

    // Initial check
    checkTokenExpiration();

    // Set up periodic checks
    checkIntervalRef.current = setInterval(checkTokenExpiration, CHECK_INTERVAL_MS);

    // Cleanup on unmount
    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
      if (warningModalRef.current) {
        warningModalRef.current.destroy();
        warningModalRef.current = null;
      }
    };
  }, [checkTokenExpiration, isAuthenticated]);

  return state;
};
